// Shim module to satisfy packages expecting `anumber`, `concatBytes`, `randomBytes` from `@noble/hashes/utils`
// Implements a compatible subset used by transitive deps during bundling.

export function anumber(n: number): void {
  if (typeof n !== "number" || !Number.isFinite(n)) {
    throw new Error("number expected");
  }
}

// Array where index 0xf0 (240) is mapped to string 'f0'
const hexes = Array.from({ length: 256 }, (_, i) =>
  i.toString(16).padStart(2, "0"),
);

export function bytesToHex(bytes: Uint8Array): string {
  let hex = "";
  for (let i = 0; i < bytes.length; i++) hex += hexes[bytes[i]];
  return hex;
}

export function hexToBytes(hex: string): Uint8Array {
  if (typeof hex !== "string") throw new Error("hex string expected");
  const hl = hex.length;
  if (hl % 2)
    throw new Error(`hex string expected, got unpadded hex of length ${hl}`);
  const al = hl / 2;
  const array = new Uint8Array(al);
  for (let ai = 0, hi = 0; ai < al; ai++, hi += 2) {
    const n1 = parseInt(hex[hi], 16);
    const n2 = parseInt(hex[hi + 1], 16);
    if (Number.isNaN(n1) || Number.isNaN(n2)) {
      const ch = hex[hi] + hex[hi + 1];
      throw new Error(
        `hex string expected, got non-hex character "${ch}" at index ${hi}`,
      );
    }
    array[ai] = n1 * 16 + n2;
  }
  return array;
}

export function concatBytes(...arrays: Uint8Array[]): Uint8Array {
  let sum = 0;
  for (let i = 0; i < arrays.length; i++) sum += arrays[i].length;
  const res = new Uint8Array(sum);
  for (let i = 0, pad = 0; i < arrays.length; i++) {
    res.set(arrays[i], pad);
    pad += arrays[i].length;
  }
  return res;
}

export function randomBytes(bytesLength = 32): Uint8Array {
  const g = globalThis as unknown as {
    crypto?: { getRandomValues?: (arr: Uint8Array) => Uint8Array };
    require?: (m: string) => {
      randomBytes?: (n: number) => Uint8Array | Buffer;
    };
  };
  if (g && g.crypto && typeof g.crypto.getRandomValues === "function") {
    return g.crypto.getRandomValues(new Uint8Array(bytesLength));
  }
  try {
    // Fallback for Node.js environments during SSR/build
    const nodeCrypto = (
      g && typeof (g as Record<string, unknown>).require === "function"
        ? (g as unknown as { require: (m: string) => unknown }).require(
            "crypto",
          )
        : null
    ) as { randomBytes?: (n: number) => Uint8Array | Buffer } | null;
    if (nodeCrypto && typeof nodeCrypto.randomBytes === "function") {
      const buf = nodeCrypto.randomBytes(bytesLength) as unknown as
        | Uint8Array
        | Buffer;
      return buf instanceof Uint8Array ? buf : Uint8Array.from(buf as Buffer);
    }
  } catch {
    // ignore
  }
  throw new Error("crypto.getRandomValues must be defined");
}

export function abytes(item: unknown): void {
  const isU8 =
    item instanceof Uint8Array ||
    (ArrayBuffer.isView(item as ArrayBufferView) &&
      (item as ArrayBufferView).constructor.name === "Uint8Array");
  if (!isU8) throw new Error("Uint8Array expected");
}

export function utf8ToBytes(str: string): Uint8Array {
  if (typeof str !== "string") throw new Error("string expected");
  return new TextEncoder().encode(str);
}

export function createView(arr: Uint8Array): DataView {
  return new DataView(arr.buffer, arr.byteOffset, arr.byteLength);
}

export function ahash(val: unknown): void {
  let outputLen: unknown = undefined;
  if (typeof val === "function") {
    // Try to read static property
    outputLen = (val as unknown as { outputLen?: unknown }).outputLen;
    if (outputLen === undefined) {
      try {
        const instance = (val as () => { outputLen?: unknown })();
        outputLen = instance?.outputLen;
      } catch {
        // ignore
      }
    }
  }
  if (!Number.isSafeInteger(outputLen as number))
    throw new Error("hash function expected");
}
